import { cn } from '@repo/ui/lib/utils';
import { ChevronDown, Lock } from 'lucide-react';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import type { FormSteps } from '@/lib/report';

type StepEntry = {
  key: string;
  index: number;
  shortTitle: string;
  title: string;
  description: string;
};

type StepAccordionProps = {
  steps: StepEntry[];
  currentStep: string | null;
  onNavigate: (step: FormSteps) => void;
  /** Sidebar order includes a report-type row as #1 — form steps are numbered from 2. */
  stepNumberOffset: number;
  lockedSteps: Partial<Record<FormSteps, boolean>>;
};

export function StepAccordion({
  steps,
  currentStep,
  onNavigate,
  stepNumberOffset,
  lockedSteps,
}: StepAccordionProps) {
  return (
    <div className="flex flex-col gap-1">
      <AccordionPrimitive.Root
        className="flex flex-col gap-1"
        onValueChange={(val) => {
          if (!val) return;
          if (lockedSteps[val as FormSteps]) return;
          onNavigate(val as FormSteps);
        }}
        type="single"
        value={currentStep ?? undefined}
      >
        {steps.map((step) => {
          const isActive = step.key === currentStep;
          const currentEntry = steps.find((s) => s.key === currentStep);
          const isPast = currentEntry ? step.index < currentEntry.index : false;
          const locked = !!lockedSteps[step.key as FormSteps];
          const displayIndex = step.index + stepNumberOffset;

          return (
            <AccordionPrimitive.Item
              className={cn(
                'rounded-lg border transition-colors',
                locked && 'bg-muted/30 opacity-80',
                isActive && !locked ? 'border-primary/40 bg-primary/5' : 'border-border bg-card',
              )}
              key={step.key}
              value={step.key}
            >
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger
                  className={cn(
                    'group flex flex-1 items-center gap-2.5 px-3 py-2.5 text-start font-medium text-sm transition-colors',
                    locked && 'cursor-not-allowed opacity-90',
                    isActive && !locked
                      ? 'text-primary'
                      : isPast && !locked
                        ? 'text-muted-foreground/80'
                        : 'text-muted-foreground/50',
                  )}
                  disabled={locked}
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-semibold text-[10px]',
                      locked && 'bg-muted text-muted-foreground',
                      !locked && isActive
                        ? 'bg-primary text-primary-foreground'
                        : !locked && isPast
                          ? 'bg-primary/20 text-primary'
                          : !locked && 'bg-muted text-muted-foreground',
                    )}
                  >
                    {locked ? <Lock className="h-3 w-3" /> : displayIndex}
                  </span>
                  <span className="flex-1 leading-tight">{step.shortTitle}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>

              <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="px-3 pt-1 pb-3 text-muted-foreground text-xs leading-relaxed">
                  {step.description}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          );
        })}
      </AccordionPrimitive.Root>
    </div>
  );
}
